import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import partnersService from "../services/partners.service";
import { enrollmentServiceClient } from "../services/enrollment.service";

export const usePartners = (params) => {
  return useQuery({
    queryKey: ["partners", params],
    queryFn: () => partnersService.listPartners(params),
    keepPreviousData: true,
  });
};

export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => partnersService.createPartner(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const useUpdatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => partnersService.updatePartner(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const useDeletePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => partnersService.deletePartner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const useTogglePartnerActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nextActive }) =>
      partnersService.togglePartnerActive(id, nextActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const useAssignPartnerUsers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }) => partnersService.assignUsers(id, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const useUnassignPartnerUsers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }) => partnersService.unassignUsers(id, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
};

export const usePartnerById = (id) => {
  return useQuery({
    queryKey: ["partners", "detail", id],
    queryFn: () => partnersService.getPartnerById(id),
    enabled: !!id,
  });
};

export const usePartnerMembers = (partnerId, params) => {
  return useQuery({
    queryKey: ["partners", "members", partnerId, params],
    queryFn: () => partnersService.getPartnerMembers(partnerId, params),
    enabled: !!partnerId,
  });
};

export const useBulkEnrollMembers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, membershipTypeId, courseId, courseIds, file }) =>
      enrollmentServiceClient.bulkEnrollMembers({
        partnerId,
        membershipTypeId,
        courseId,
        courseIds,
        file,
      }),
    onSuccess: (_data, { partnerId }) => {
      if (partnerId) {
        queryClient.invalidateQueries({
          queryKey: ["partners", "members", partnerId],
        });
      }
    },
  });
};
